// Layar 🔐 Hak Akses — pohon Menu → Sub-menu → Aksi, tiap baris punya pilihan
// ⛔ tidak bisa akses · 👁️ lihat saja · ✏️ bisa ubah.
//
// Patokannya GRUP (peran). "Per Karyawan" hanya menyimpan yang BEDA dari grupnya, sehingga
// perbaikan setelan grup tetap sampai ke semua orang yang tak dioverride.
import { esc } from '@domain/format';
import { MENU_META } from '@core/shell';
import { toast } from '@ui/toast';
import { levelOf, cariBerlapis, LEVEL_LABEL, type Level, type PetaAkses } from '@domain/perms';
import { AKSI_MENU, BELUM_ADA, TERKUNCI, DITEGAKKAN, type Simpul } from '@domain/perms.registry';
import { aksesRole, aksesUser, simpanAksesRole, simpanAksesUser, bawaanUntuk } from '@core/perms';
import { fetchSetting } from '@core/settings';
import { currentUser } from '@core/session';
import { listKaryawan, type KaryawanRow } from '@modules/karyawan/karyawan.repo';

const SEMUA_ROLE = ['Manager', 'SPV', 'Admin Finance', 'CS', 'Teknisi'];
// Hanya Owner yang boleh menyentuh hak Manager — kalau tidak, setiap batasan yang Owner pasang
// pada Manager bersifat sukarela: Manager tinggal membuka kembali haknya sendiri di layar ini.
const roleList = (): string[] => (currentUser()?.role === 'Owner' ? SEMUA_ROLE : SEMUA_ROLE.filter((r) => r !== 'Manager'));
const bolehAturOrang = (k: KaryawanRow): boolean => {
  const u = currentUser();
  if (k.role === 'Owner' || k.role === 'Supplier') return false;
  if (u?.role === 'Owner') return true;
  return k.role !== 'Manager' && k.id !== u?.id;      // tak boleh menaikkan hak diri sendiri
};
const LEVELS: Level[] = ['none', 'read', 'full'];

let mode: 'grup' | 'user' = 'grup';
let role = 'Manager';
let empId: number | null = null;
let karyawan: KaryawanRow[] = [];
let draft: PetaAkses = {};          // perubahan yang belum disimpan
let terbuka: Record<string, boolean> = {};

/** Pohon untuk satu peran: menu yang boleh dilihat peran itu + sub-menu + aksi + baris "belum ada". */
function pohon(r: string): { grup: string; menu: Simpul[] }[] {
  const out: { grup: string; menu: Simpul[] }[] = [];
  MENU_META().forEach((m) => {
    if (m.roles && !m.roles.includes(r as never)) return;
    if (m.roles?.includes('Supplier' as never)) return;      // portal supplier diatur terpisah
    const anak = [...(AKSI_MENU[m.id] ?? []), ...(BELUM_ADA[m.id] ?? [])];
    let g = out.find((x) => x.grup === m.group);
    if (!g) { g = { grup: m.group, menu: [] }; out.push(g); }
    g.menu.push(anak.length ? { key: m.id, label: m.label, anak } : { key: m.id, label: m.label });
  });
  return out;
}

const tersimpan = (): PetaAkses =>
  mode === 'grup' ? (aksesRole()[role] ?? {}) : (empId != null ? (aksesUser()[String(empId)] ?? {}) : {});

/** Level yang sedang tampil di layar (termasuk perubahan yang belum disimpan). */
function levelTampil(key: string): Level {
  const dasar = mode === 'user' ? (aksesRole()[role] ?? {}) : undefined;
  const cari = cariBerlapis(draft, tersimpan(), dasar);
  return levelOf(key, (k) => cari(k) ?? bawaanUntuk(role, k));
}

/** true bila baris ini berbeda dari yang diwariskan (grup / bawaan peran) → diberi tanda. */
function adaOverride(key: string): boolean {
  const map = { ...tersimpan(), ...draft };
  return map[key] !== undefined;
}

function barisHTML(s: Simpul, dalam: number, canEdit: boolean): string {
  const lvl = levelTampil(s.key);
  const punyaAnak = !!s.anak?.length;
  const buka = terbuka[s.key] !== false;
  const pad = 10 + dalam * 18;
  const warna = lvl === 'none' ? 'var(--danger)' : lvl === 'read' ? 'var(--warn)' : 'var(--ok)';
  if (s.belum) {
    return `<div class="hk-row" style="padding-left:${pad}px;opacity:.55"><span class="hk-lbl">${esc(s.label)}</span>
      <span class="pill" style="background:var(--ice);color:var(--muted)">belum ada fiturnya</span></div>`;
  }
  const kunci = (TERKUNCI[role] ?? []).includes(s.key);
  const pilih = `<select class="hk-lvl" data-key="${esc(s.key)}" style="border-color:${warna};color:${warna}" ${canEdit && !kunci ? '' : 'disabled'}>${
    LEVELS.map((l) => `<option value="${l}" ${lvl === l ? 'selected' : ''}>${LEVEL_LABEL[l]}</option>`).join('')}</select>`;
  const catatan = kunci
    ? '<span class="pill hk-lock" title="Dikunci: pemisahan tugas anti-kecurangan — penandaan lunas dan verifikasi tak boleh ada pada satu orang">🔒 dikunci</span>'
    : (s.key.includes('.') && !DITEGAKKAN.has(s.key)
      ? '<span class="pill hk-soon" title="Sudah bisa disetel, tapi tombolnya belum membacanya — sedang disambungkan bertahap">belum ditegakkan</span>' : '');
  const tanda = adaOverride(s.key) ? `<span class="pill hk-ovr" title="Berbeda dari ${mode === 'user' ? 'setelan grup' : 'bawaan peran'}">diatur</span>` : '';
  const panah = punyaAnak ? `<button class="hk-tgl" data-tgl="${esc(s.key)}">${buka ? '▾' : '▸'}</button>` : '<span class="hk-tgl" style="visibility:hidden">▾</span>';
  const anakHTML = punyaAnak && buka ? s.anak!.map((a) => barisHTML(a, dalam + 1, canEdit)).join('') : '';
  return `<div class="hk-row${dalam === 0 ? ' hk-menu' : ''}" style="padding-left:${pad}px">${panah}
      <span class="hk-lbl">${esc(s.label)}</span>${catatan}${tanda}${pilih}</div>${anakHTML}`;
}

export function hakAksesTab(body: HTMLElement, canEdit: boolean): void {
  const gambar = (): void => {
    if (!roleList().includes(role)) role = roleList()[0] ?? 'SPV';
    const perUser = mode === 'user';
    const emp = karyawan.find((k) => k.id === empId);
    if (perUser && emp) role = emp.role;
    const isi = pohon(role).map((g) => `<div class="hk-grp"><div class="hk-grp-h">${esc(g.grup)}</div>${
      g.menu.map((m) => barisHTML(m, 0, canEdit)).join('')}</div>`).join('');
    const jml = Object.keys({ ...tersimpan(), ...draft }).length;

    body.innerHTML = `<div class="scope-note">Patokannya <b>grup (peran)</b>; tiap orang bisa dikecualikan di tab <b>Per Karyawan</b>.
      Menyetel sebuah menu ke <b>👁️ lihat saja</b> otomatis membuat semua tambah/ubah/hapus di dalamnya ikut lihat-saja.
      <b>Owner selalu bisa semuanya.</b> Data yang terlihat tetap dibatasi wilayah & kepemilikan masing-masing.</div>
      <div class="toolbar hk-bar">
        <button class="btn ${mode === 'grup' ? 'primary' : ''}" id="hk_m_grup">👥 Per Grup</button>
        <button class="btn ${perUser ? 'primary' : ''}" id="hk_m_user">👤 Per Karyawan</button>
        ${perUser
          ? `<select id="hk_emp">${karyawan.filter(bolehAturOrang).map((k) => `<option value="${k.id}" ${empId === k.id ? 'selected' : ''}>${esc(k.nama)} · ${esc(k.role)}</option>`).join('')}</select>`
          : `<select id="hk_role">${roleList().map((r) => `<option ${role === r ? 'selected' : ''}>${esc(r)}</option>`).join('')}</select>`}
        <span class="pill" style="background:var(--ice);color:var(--navy)">${jml} baris diatur</span>
        ${canEdit ? `<span style="margin-left:auto;display:flex;gap:6px"><button class="btn" id="hk_reset">↺ Ikut bawaan</button><button class="btn primary" id="hk_save">💾 Simpan</button></span>` : ''}
      </div>
      ${perUser ? `<div class="scope-note" style="background:var(--warnbg)">Yang diubah di sini <b>hanya berlaku untuk ${esc(emp?.nama ?? '—')}</b> dan menimpa setelan grup ${esc(role)}.</div>` : ''}
      <div class="hk-tree">${isi}</div>`;

    body.querySelector('#hk_m_grup')?.addEventListener('click', () => { if (bolehPindah()) { mode = 'grup'; draft = {}; gambar(); } });
    body.querySelector('#hk_m_user')?.addEventListener('click', () => {
      if (!bolehPindah()) return;
      mode = 'user'; draft = {};
      empId ??= karyawan.find(bolehAturOrang)?.id ?? null;
      gambar();
    });
    body.querySelector('#hk_role')?.addEventListener('change', (e) => { if (bolehPindah()) { role = (e.target as HTMLSelectElement).value; draft = {}; } gambar(); });
    body.querySelector('#hk_emp')?.addEventListener('change', (e) => { if (bolehPindah()) { empId = Number((e.target as HTMLSelectElement).value); draft = {}; } gambar(); });
    body.querySelectorAll<HTMLElement>('.hk-tgl[data-tgl]').forEach((b) => b.addEventListener('click', () => {
      const k = b.dataset['tgl']!; terbuka[k] = terbuka[k] === false; gambar();
    }));
    body.querySelectorAll<HTMLSelectElement>('.hk-lvl').forEach((s) => s.addEventListener('change', () => {
      draft[s.dataset['key']!] = s.value as Level; gambar();       // gambar ulang: anak ikut terjepit induknya
    }));
    body.querySelector('#hk_reset')?.addEventListener('click', async () => {
      if (!confirm(`Kembalikan hak akses ${perUser ? esc(emp?.nama ?? '') : role} ke bawaan?`)) return;
      try {
        if (perUser) { const all = { ...(await fetchSetting<Record<string, PetaAkses>>('akses_user', {})) }; delete all[String(empId)]; await simpanAksesUser(all); }
        else { const all = { ...(await fetchSetting<Record<string, PetaAkses>>('akses', {})) }; delete all[role]; await simpanAksesRole(all); }
        draft = {}; toast('↺ Kembali ke bawaan'); gambar();
      } catch (e) { toast((e as Error).message); }
    });
    body.querySelector('#hk_save')?.addEventListener('click', async () => {
      // Simpan sparse: buang baris yang nilainya sama dengan warisannya, supaya perbaikan
      // bawaan/grup berikutnya tetap sampai ke orang ini.
      if (mode === 'user' && empId == null) { toast('Pilih karyawan dulu.'); return; }
      const terkunci = TERKUNCI[role] ?? [];
      if (Object.keys(draft).some((k) => terkunci.includes(k))) { toast('🔒 Baris itu dikunci demi pemisahan tugas — tak bisa diubah.'); return; }
      const gabung = { ...tersimpan(), ...draft };
      const bersih: PetaAkses = {};
      for (const [k, v] of Object.entries(gabung)) {
        const waris = mode === 'user'
          ? levelOf(k, (x) => (aksesRole()[role] ?? {})[x] ?? bawaanUntuk(role, x))
          : levelOf(k, (x) => bawaanUntuk(role, x));
        if (waris !== v) bersih[k] = v;
      }
      try {
        // Baca ULANG dari server sebelum menulis: peta ini dipegang seluruh peran, dan menyimpan
        // dari salinan lama akan menghapus pembatasan yang baru dibuat admin lain tanpa jejak.
        if (mode === 'user') {
          const all = { ...(await fetchSetting<Record<string, PetaAkses>>('akses_user', {})) };
          if (Object.keys(bersih).length) all[String(empId)] = bersih; else delete all[String(empId)];
          await simpanAksesUser(all);
        } else {
          const all = { ...(await fetchSetting<Record<string, PetaAkses>>('akses', {})) };
          if (Object.keys(bersih).length) all[role] = bersih; else delete all[role];
          await simpanAksesRole(all);
        }
        draft = {}; toast('✅ Hak akses disimpan'); gambar();
      } catch (e) { toast((e as Error).message); }
    });
  };
  const bolehPindah = (): boolean => !Object.keys(draft).length || confirm('Perubahan belum disimpan. Tinggalkan?');

  body.innerHTML = `<div class="panel"><div class="empty">Memuat…</div></div>`;
  void listKaryawan().then((k) => { karyawan = k; gambar(); }).catch((e: Error) => {
    karyawan = []; gambar(); toast(e.message);
  });
}
